import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  DesignV2Scope,
  EmptyState,
  ListRow,
  Menu,
} from "../design-v2";
import { FilterRail, StackHeader } from "../components/product";
import { formatRelativeCreatedLabel } from "../features/weeklyPlans/formatters";
import { buildOrigin, resolveBackTarget } from "../features/weeklyPlans/navigation";
import { getWeeklyPlanDetailsPath } from "../features/weeklyPlans/routes";
import { mapWeeklyPlanListItemToUi } from "../features/weeklyPlans/mappers";
import {
  bookmarkWeeklyPlan,
  getWeeklyPlans,
  unbookmarkWeeklyPlan,
} from "../services/api";
import {
  BookmarkIcon,
  LIBRARY_STATUS_FILTERS,
  LibraryLoading,
  LibrarySearchField,
  PlanMetadata,
  PlusIcon,
  SlidersIcon,
  SparkleIcon,
} from "./programLibraryShared";
import "./programLibrary.css";

export default function AllPrograms() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadWeeklyPlans() {
      setIsLoading(true);

      try {
        const response = await getWeeklyPlans();
        const items = (response.items || []).map((item) =>
          mapWeeklyPlanListItemToUi(item, formatRelativeCreatedLabel(item.createdAt))
        );

        if (isMounted) {
          setPrograms(items);
        }
      } catch (error) {
        if (isMounted) {
          setPrograms([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadWeeklyPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredPrograms = useMemo(() => {
    const matchingPrograms = programs.filter((program) => {
      const matchesFilter =
        activeFilter === "all" ? true : program.status === activeFilter;

      const matchesSearch = program.name
        .toLowerCase()
        .includes(searchQuery.trim().toLowerCase());

      return matchesFilter && matchesSearch;
    });

    const hasBookmarks = matchingPrograms.some((program) => program.isBookmarked);

    return [...matchingPrograms].sort((left, right) => {
      if (hasBookmarks && left.isBookmarked !== right.isBookmarked) {
        return left.isBookmarked ? -1 : 1;
      }

      return new Date(right.createdAt) - new Date(left.createdAt);
    });
  }, [activeFilter, programs, searchQuery]);

  const handleBookmarkToggle = async (event, program) => {
    event.stopPropagation();

    const nextBookmarkState = !program.isBookmarked;
    setPrograms((prev) =>
      prev.map((item) =>
        item.id === program.id ? { ...item, isBookmarked: nextBookmarkState } : item
      )
    );

    try {
      if (nextBookmarkState) {
        await bookmarkWeeklyPlan(program.weeklyPlanParentId);
      } else {
        await unbookmarkWeeklyPlan(program.weeklyPlanParentId);
      }
    } catch (error) {
      setPrograms((prev) =>
        prev.map((item) =>
          item.id === program.id ? { ...item, isBookmarked: program.isBookmarked } : item
        )
      );
    }
  };

  function navigateToManualNew() {
    navigate("/program/manual-new", {
      state: {
        from: buildOrigin(location),
        returnTo: resolveBackTarget(location, "/program"),
      },
    });
  }

  return (
    <DesignV2Scope className="lz-library" data-testid="all-programs-root">
      <StackHeader
        title="All Programs"
        backLabel="Back"
        onBack={() => navigate(resolveBackTarget(location, "/program"))}
        trailing={
          <Menu
            open={isCreateMenuOpen}
            onOpenChange={setIsCreateMenuOpen}
            trigger={
              <Button
                size="icon"
                className="lz-library__create-trigger"
                aria-label="Create program"
              >
                <PlusIcon />
              </Button>
            }
            menuClassName="lz-library__create-menu"
            aria-label="Create program options"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() =>
                navigate("/ai", {
                  state: {
                    from: buildOrigin(location),
                  },
                })
              }
            >
              <SparkleIcon />
              AI builder
            </button>
            <button type="button" role="menuitem" onClick={navigateToManualNew}>
              <SlidersIcon />
              Manual
            </button>
          </Menu>
        }
      />

      <main className="lz-library__content">
        <LibrarySearchField
          value={searchQuery}
          onChange={setSearchQuery}
          label="Search your library"
        />

        <FilterRail
          label="Program status filters"
          options={LIBRARY_STATUS_FILTERS}
          value={activeFilter}
          onChange={setActiveFilter}
        />

        {isLoading ? (
          <LibraryLoading label="Loading your programs" />
        ) : filteredPrograms.length === 0 ? (
          <EmptyState
            className="lz-library__empty"
            title="No programs yet"
            body="Create your first weekly template to populate your library."
            action={
              <Button className="lz-library__empty-action" onClick={navigateToManualNew}>
                Create your first program
              </Button>
            }
          />
        ) : (
          <Card padding="none" className="lz-library__list" data-testid="all-programs-list">
            {filteredPrograms.map((program) => (
              <div
                className="lz-library__row"
                data-testid="all-programs-row"
                data-bookmarked={program.isBookmarked ? "true" : "false"}
                key={program.id}
              >
                <ListRow
                  className="lz-library__row-main"
                  title={program.name}
                  description={`${program.frequencyPerWeek} workouts / week \u2022 ${program.totalWeeklySets} total sets`}
                  meta={
                    <>
                      <PlanMetadata source={program.source} status={program.status} />
                      <span className="lz-library__created">{program.createdLabel}</span>
                    </>
                  }
                  onClick={() =>
                    navigate(getWeeklyPlanDetailsPath(program.weeklyPlanParentId), {
                      state: {
                        from: buildOrigin(location),
                        returnTo: resolveBackTarget(location, "/program"),
                      },
                    })
                  }
                />
                <button
                  type="button"
                  className="lz-library__bookmark"
                  aria-label={program.isBookmarked ? "Remove bookmark" : "Add bookmark"}
                  aria-pressed={program.isBookmarked}
                  onClick={(event) => handleBookmarkToggle(event, program)}
                >
                  <BookmarkIcon filled={program.isBookmarked} />
                </button>
              </div>
            ))}
          </Card>
        )}
      </main>
    </DesignV2Scope>
  );
}
